
import React, { useState } from 'react';
import { User, SubmissionData, Brand } from '../../../types';
import { Department } from '../../../enums';
import { User as UserIcon, Mail, Lock, Save, Shield, Upload, X, Clock, Activity, List, LogIn, Store } from 'lucide-react';

interface ProfileViewProps {
  currentUser: User;
  onUpdateUser: (user: User) => void;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  submissions: SubmissionData[];
  brands: Brand[];
}

export const ProfileView: React.FC<ProfileViewProps> = ({ currentUser, onUpdateUser, showToast, submissions, brands }) => {
  const [activeTab, setActiveTab] = useState<'settings' | 'history'>('settings');
  const [formData, setFormData] = useState<User>({ ...currentUser });
  const [showPassword, setShowPassword] = useState(false);

  const handleSave = () => {
    if (!formData.name || !formData.email || !formData.username) {
        showToast('error', 'Error', 'Name, Email and Username are required.');
        return;
    }
    
    // Update the user state
    onUpdateUser(formData);
    
    showToast('success', 'Profile Updated', 'Your profile details have been saved.');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onloadend = () => {
              setFormData({ ...formData, profileImage: reader.result as string });
          };
          reader.readAsDataURL(file);
      }
  };

  const removeImage = () => {
      setFormData({...formData, profileImage: undefined});
  };

  // Filter submissions where this user performed an action
  const myActivity = submissions.filter(sub => 
      sub.workflowLogs?.some(log => log.user === currentUser.name)
  ).sort((a, b) => {
      // Sort by the timestamp of the LATEST action performed by THIS user
      const logsA = a.workflowLogs?.filter(l => l.user === currentUser.name) || [];
      const latestA = logsA.length > 0 ? logsA[logsA.length - 1].timestamp : (a.timestamp || '');
      
      const logsB = b.workflowLogs?.filter(l => l.user === currentUser.name) || [];
      const latestB = logsB.length > 0 ? logsB[logsB.length - 1].timestamp : (b.timestamp || '');

      return new Date(latestB).getTime() - new Date(latestA).getTime();
  });

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
             <div>
                <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">My Profile</h2>
                <p className="text-zinc-500 text-sm">Manage your account details and view activity.</p>
             </div>
             
             <div className="bg-white rounded-lg p-1 border border-zinc-200 flex">
                <button 
                    onClick={() => setActiveTab('settings')}
                    className={`px-4 py-2 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${activeTab === 'settings' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                    <UserIcon className="w-3.5 h-3.5" /> Settings
                </button>
                <button 
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-2 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${activeTab === 'history' ? 'bg-zinc-900 text-white shadow' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                    <Activity className="w-3.5 h-3.5" /> My Activity
                </button>
            </div>
        </div>

        {activeTab === 'settings' ? (
            <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-8">
                <div className="flex flex-col md:flex-row items-start gap-8 mb-8">
                    {/* Image Upload */}
                    <div className="shrink-0 flex flex-col items-center">
                        <label className="relative cursor-pointer group">
                            <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                            <div className="w-32 h-32 bg-zinc-100 rounded-full flex items-center justify-center text-zinc-300 text-2xl font-bold border-4 border-white shadow-lg overflow-hidden group-hover:border-teal-100 transition-all">
                                {formData.profileImage ? (
                                    <img src={formData.profileImage} alt="current" className="w-full h-full object-cover" />
                                ) : (
                                    formData.name.charAt(0)
                                )}
                            </div>
                            <div className="absolute bottom-1 right-1 bg-zinc-900 p-2 rounded-full text-white shadow-md group-hover:scale-110 transition-transform">
                                <Upload className="w-4 h-4" />
                            </div>
                            {formData.profileImage && (
                                <button 
                                    onClick={(e) => { e.preventDefault(); removeImage(); }}
                                    className="absolute top-1 right-1 bg-red-500 p-1.5 rounded-full text-white shadow-md hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </label>
                        <span className="text-xs font-bold text-zinc-400 mt-3 uppercase tracking-wide">Profile Photo</span>
                    </div>

                    <div className="flex-1 w-full">
                        <div className="flex items-center gap-3 mb-6">
                            <h3 className="text-xl font-bold text-zinc-900">{formData.name}</h3>
                            <div className="flex gap-2">
                                <span className="text-xs font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded-full border border-teal-100">{formData.role}</span>
                                <span className="text-xs font-bold text-zinc-500 bg-zinc-100 px-3 py-1 rounded-full border border-zinc-200">{formData.privilege}</span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><UserIcon className="w-3 h-3" /> Full Name</label>
                                    <input 
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData({...formData, name: e.target.value})}
                                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><UserIcon className="w-3 h-3" /> Username</label>
                                    <input 
                                        type="text"
                                        value={formData.username}
                                        onChange={e => setFormData({...formData, username: e.target.value})}
                                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><Mail className="w-3 h-3" /> Email Address</label>
                                <input 
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><Lock className="w-3 h-3" /> Password</label>
                                <div className="relative">
                                    <input 
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={e => setFormData({...formData, password: e.target.value})}
                                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold focus:ring-2 focus:ring-teal-100 outline-none pr-12"
                                    />
                                    <button 
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-3.5 text-xs font-bold text-zinc-400 hover:text-teal-600"
                                    >
                                        {showPassword ? 'HIDE' : 'SHOW'}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 pt-6 border-t border-zinc-100">
                                <div className="space-y-2 opacity-60 cursor-not-allowed">
                                    <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><Shield className="w-3 h-3" /> Department</label>
                                    <input 
                                        type="text"
                                        value={formData.department}
                                        disabled
                                        className="w-full bg-zinc-100 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-500 outline-none"
                                    />
                                    <p className="text-[10px] text-zinc-400">Department cannot be changed by yourself.</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-zinc-500 uppercase flex items-center gap-2"><Store className="w-3 h-3" /> Assigned Brands</label>
                                    <div className="flex flex-wrap gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-xl min-h-[50px]">
                                        {formData.brandIds && formData.brandIds.length > 0 ? (
                                            formData.brandIds.map(brandId => {
                                                const brand = brands.find(b => b.id === brandId);
                                                return (
                                                    <span key={brandId} className="px-3 py-1 bg-white text-zinc-700 text-xs font-bold rounded-lg border border-zinc-200 shadow-sm flex items-center gap-2">
                                                        {brand?.logo && <img src={brand.logo} alt="" className="w-4 h-4 rounded-full object-cover" />}
                                                        {brand ? brand.name : 'Unknown Brand'}
                                                    </span>
                                                );
                                            })
                                        ) : (
                                            <span className="text-sm text-zinc-400 italic">No brands assigned</span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-zinc-400">Brands are assigned by administrators.</p>
                                </div>
                            </div>

                            <div className="pt-4">
                                <button 
                                    onClick={handleSave}
                                    className="w-full py-4 bg-zinc-900 text-white font-bold rounded-xl shadow-lg shadow-zinc-900/10 hover:bg-zinc-800 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <Save className="w-5 h-5" /> Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ) : (
            <div className="space-y-4 animate-fade-in">
                {myActivity.length === 0 ? (
                    <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm p-12 text-center text-zinc-400">
                        <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <h3 className="font-bold text-lg text-zinc-700">No Activity Recorded</h3>
                        <p className="text-sm">You haven't interacted with any submissions yet.</p>
                    </div>
                ) : (
                    myActivity.map(sub => {
                        const myLogs = sub.workflowLogs?.filter(l => l.user === currentUser.name) || [];
                        
                        return (
                            <div key={sub.submissionId} className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6">
                                <div className="flex justify-between items-start border-b border-zinc-100 pb-4 mb-4">
                                    <div>
                                        <h4 className="font-bold text-zinc-900 text-sm">Submission #{sub.submissionId}</h4>
                                        <p className="text-xs text-zinc-500">{sub.brands.join(', ')} • {sub.items.length} Items</p>
                                    </div>
                                    <span className="text-xs font-bold bg-zinc-100 text-zinc-600 px-2 py-1 rounded">
                                        {sub.status}
                                    </span>
                                </div>
                                <div className="space-y-3">
                                    {myLogs.map(log => (
                                        <div key={log.id} className="flex gap-3 text-sm">
                                            <div className="mt-1">
                                                {log.action === 'Approve' && <div className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />}
                                                {log.action === 'Reject' && <div className="w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-100" />}
                                                {log.action === 'Comment' && <div className="w-2 h-2 rounded-full bg-teal-500 ring-2 ring-teal-100" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center">
                                                    <span className={`font-bold ${log.action === 'Approve' ? 'text-emerald-700' : log.action === 'Reject' ? 'text-red-600' : 'text-zinc-700'}`}>
                                                        {log.action}
                                                    </span>
                                                    <span className="text-xs text-zinc-400 flex items-center gap-1">
                                                        <Clock className="w-3 h-3" /> {log.timestamp}
                                                    </span>
                                                </div>
                                                <p className="text-zinc-600 text-xs mt-1 bg-zinc-50 p-3 rounded-lg border border-zinc-100 italic">
                                                    "{log.comment}"
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
        )}
    </div>
  );
};
